import React, { useEffect, useState } from 'react';
import { X, Download } from 'lucide-react';

const DISMISS_KEY = 'pwa_install_dismissed_at';
const DISMISS_TTL_DAYS = 14;

/**
 * Cross-platform PWA install prompt.
 *  • Android/Chrome → captures beforeinstallprompt and calls prompt() on click
 *  • iOS Safari → shows manual "Add to Home Screen" instructions
 *  • Hidden when already installed or recently dismissed.
 *  • iter176 — Hidden entirely on public marketing routes (/offer, /promo/*,
 *    /login, /sms-compliance, /terms, /privacy) so customers scanning a QR
 *    code on a feather flag never see the internal install pitch.
 */

// iter176 — Path prefixes that suppress the install prompt. Add new public
// marketing routes here as they ship.
const PUBLIC_ROUTE_PREFIXES = [
  '/offer',
  '/promo/',
  '/login',
  '/sms-compliance',
  '/sms-consent',
  '/public/',
  '/terms',
  '/privacy',
];

const isPublicMarketingRoute = (pathname) => {
  if (!pathname) return false;
  if (pathname === '/offer') return true;
  return PUBLIC_ROUTE_PREFIXES.some((p) => pathname.startsWith(p));
};

const PwaInstallPrompt = () => {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    // iter176 — Public marketing routes never see the install prompt.
    // QR scans on feather flags / yard signs / business cards land here.
    if (isPublicMarketingRoute(window.location.pathname)) return;
    // Respect recent dismissal
    try {
      const ts = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
      if (ts && Date.now() - ts < DISMISS_TTL_DAYS * 24 * 3600 * 1000) return;
    } catch { /* ignore */ }

    // Already installed (running standalone)?
    const standalone =
      window.matchMedia?.('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;
    if (standalone) return;

    // Android/Chrome — capture install prompt
    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // iOS Safari — show manual instructions (no beforeinstallprompt support)
    const ua = window.navigator.userAgent;
    const isIos = /iPhone|iPad|iPod/i.test(ua);
    const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
    if (isIos && isSafari) {
      setIosHint(true);
      // Delay show by 4 s so we don't block first paint
      const t = setTimeout(() => setVisible(true), 4000);
      return () => { clearTimeout(t); window.removeEventListener('beforeinstallprompt', handler); };
    }
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
    setVisible(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const choice = await deferred.userChoice;
    if (choice?.outcome === 'accepted' || choice?.outcome === 'dismissed') {
      dismiss();
    }
  };

  if (!visible) return null;
  // iter176 — Belt-and-braces: even if state somehow leaks across SPA
  // navigation, never render the prompt on a public marketing route.
  if (typeof window !== 'undefined' && isPublicMarketingRoute(window.location.pathname)) return null;

  return (
    <div
      data-testid="pwa-install-prompt"
      className="fixed inset-x-0 bottom-0 z-[60] px-4 pb-4 pointer-events-none"
    >
      <div className="max-w-md mx-auto pointer-events-auto bg-[#141414] border border-[#D4AF37]/40 rounded-lg shadow-2xl shadow-black/40 backdrop-blur p-4 flex items-start gap-3">
        <div className="w-10 h-10 rounded-md bg-[#D4AF37]/15 flex items-center justify-center shrink-0">
          <Download className="w-5 h-5 text-[#D4AF37]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white font-medium text-sm leading-tight">Install NexFlow on your phone</p>
          {iosHint ? (
            <p className="text-[#A3A3A3] text-xs mt-1 leading-snug">
              Tap the <span className="text-white">Share</span> icon in Safari, then choose
              <span className="text-[#D4AF37]"> Add to Home Screen</span>.
            </p>
          ) : (
            <p className="text-[#A3A3A3] text-xs mt-1 leading-snug">
              Get one-tap access from your home screen — works offline for the basics.
            </p>
          )}
          {!iosHint && (
            <button
              data-testid="pwa-install-btn"
              onClick={install}
              className="mt-2 inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-[#D4AF37] text-[#0A0A0A] text-xs font-semibold hover:bg-[#F3E5AB]"
            >
              Install app
            </button>
          )}
        </div>
        <button
          data-testid="pwa-install-dismiss"
          onClick={dismiss}
          aria-label="Dismiss"
          className="text-[#A3A3A3] hover:text-white"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default PwaInstallPrompt;
